import React from "react";

const StudyGroupHero = ({
  totals,
  onEnterCode,
  onCreateGroup,
  onOpenStudyBuddies,
  onSelectTab,
}) => {
  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#2563EB] via-[#1D4ED8] to-[#1E40AF] p-6 md:p-8 text-white shadow-lg shadow-blue-900/10">
      {/* Background ambient shapes */}
      <div className="absolute -right-16 -top-16 w-64 h-64 bg-white/10 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute right-40 -bottom-20 w-48 h-48 bg-blue-400/20 rounded-full blur-xl pointer-events-none" />

      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/15 text-blue-100 backdrop-blur-md border border-white/10">
              <i className="fa-solid fa-graduation-cap text-xs text-blue-200"></i> Peer Collaboration
            </span>
            <span className="text-xs text-blue-200 font-medium">Study Circles</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
            Study Groups & Peer Circles
          </h1>
          <p className="text-blue-100 text-sm md:text-base mt-1.5 max-w-xl font-normal leading-relaxed">
            Collaborate with peers, share verified reviewers, discuss practice questions, and prepare for board exams together.
          </p>
        </div>

        {/* Primary Action Buttons */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={onEnterCode}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white shadow-sm transition-all duration-200 active:scale-95"
          >
            <i className="fa-solid fa-key text-xs"></i>
            <span>Enter Code</span>
          </button>
          <button
            onClick={onCreateGroup}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-white text-blue-700 hover:bg-blue-50 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
          >
            <i className="fa-solid fa-plus text-xs"></i>
            <span>New Group</span>
          </button>
        </div>
      </div>

      {/* Dynamic Stats Strip */}
      <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mt-7 pt-6 border-t border-white/15">
        
        {/* Total Groups Button */}
        <button
          type="button"
          onClick={() => onSelectTab && onSelectTab("all")}
          className="bg-white/10 hover:bg-white/20 active:scale-95 backdrop-blur-md rounded-2xl p-3.5 border border-white/10 hover:border-white/25 flex items-center justify-between gap-3 text-left transition-all duration-200 cursor-pointer group shadow-sm"
          title="Filter to all study groups"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 group-hover:bg-white/25 flex items-center justify-center text-white text-base shrink-0 transition-colors">
              <i className="fa-solid fa-layer-group"></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-blue-200 group-hover:text-white font-medium transition-colors">Total Groups</p>
              <p className="text-xl font-extrabold tracking-tight">{totals.totalGroups}</p>
            </div>
          </div>
          <i className="fa-solid fa-chevron-right text-xs text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0 hidden sm:block"></i>
        </button>

        {/* Created by Me Button */}
        <button
          type="button"
          onClick={() => onSelectTab && onSelectTab("owned")}
          className="bg-white/10 hover:bg-white/20 active:scale-95 backdrop-blur-md rounded-2xl p-3.5 border border-white/10 hover:border-amber-300/30 flex items-center justify-between gap-3 text-left transition-all duration-200 cursor-pointer group shadow-sm"
          title="Filter to groups created by me"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 group-hover:bg-amber-400/20 flex items-center justify-center text-amber-300 text-base shrink-0 transition-colors">
              <i className="fa-solid fa-crown"></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-blue-200 group-hover:text-white font-medium transition-colors">Created by Me</p>
              <p className="text-xl font-extrabold tracking-tight">{totals.ownedCount}</p>
            </div>
          </div>
          <i className="fa-solid fa-chevron-right text-xs text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0 hidden sm:block"></i>
        </button>

        {/* Joined Circles Button */}
        <button
          type="button"
          onClick={() => onSelectTab && onSelectTab("joined")}
          className="bg-white/10 hover:bg-white/20 active:scale-95 backdrop-blur-md rounded-2xl p-3.5 border border-white/10 hover:border-blue-300/30 flex items-center justify-between gap-3 text-left transition-all duration-200 cursor-pointer group shadow-sm"
          title="Filter to joined study circles"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 group-hover:bg-blue-400/20 flex items-center justify-center text-blue-200 text-base shrink-0 transition-colors">
              <i className="fa-solid fa-handshake"></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-blue-200 group-hover:text-white font-medium transition-colors">Joined Circles</p>
              <p className="text-xl font-extrabold tracking-tight">{totals.joinedCount}</p>
            </div>
          </div>
          <i className="fa-solid fa-chevron-right text-xs text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0 hidden sm:block"></i>
        </button>

        {/* Study Buddies Button - Click to open full buddies list */}
        <button
          type="button"
          onClick={onOpenStudyBuddies}
          className="bg-white/10 hover:bg-white/20 active:scale-95 backdrop-blur-md rounded-2xl p-3.5 border border-white/10 hover:border-emerald-300/50 flex items-center justify-between gap-3 text-left transition-all duration-200 cursor-pointer group shadow-sm ring-1 ring-emerald-400/20 hover:ring-emerald-400/50"
          title="Click to view whole list of study buddies across all groups"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 group-hover:bg-emerald-500/30 flex items-center justify-center text-emerald-300 group-hover:text-emerald-200 text-base shrink-0 transition-colors shadow-xs">
              <i className="fa-solid fa-user-group"></i>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs text-blue-200 group-hover:text-white font-medium transition-colors">Study Buddies</p>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              </div>
              <p className="text-xl font-extrabold tracking-tight text-white">{totals.totalMembers}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-emerald-300/80 group-hover:text-emerald-200 font-semibold transition-all shrink-0">
            <span className="text-[11px] hidden xl:inline">View list</span>
            <i className="fa-solid fa-chevron-right text-xs group-hover:translate-x-0.5 transition-transform"></i>
          </div>
        </button>

      </div>
    </section>
  );
};

export default StudyGroupHero;
